<script setup lang="ts">
const { t } = useI18n()
const { width } = useWindowSize()
const localeRoute = useLocaleRoute()

const swiperBreakpoints = computed(() => ({
  320: { slidesPerView: 1.1 },
  375: { slidesPerView: 1.2 },
  480: { slidesPerView: 1.5 },
  640: { slidesPerView: 1.8 },
  800: { slidesPerView: 2.2 },
  1000: { slidesPerView: 2.5 },
  1100: { slidesPerView: 2.8 },
  1280: { slidesPerView: 3 },
}))

const slidesOffsetBefore = computed(() => (width.value < 1280 ? 20 : 0))
const slidesOffsetAfter = computed(() => (width.value < 1280 ? 20 : 0))

const caseStudy = [
  {
    title: 'Lampu Festival 2024',
    img: '/images/home/lampu_festival.png',
    features: [
      { text: t('flexible-payment-system'), highlight: t('fiat-and-crypto') },
      { text: t('multiple-ticket-price-tiers') },
      { text: t('branding-landing-pages') },
      { text: t('attendee-notifications') },
    ],
    location: t('bali'),
    link: '/lampu-2024',
  },
  {
    title: 'Devcon Week 2024',
    img: '/images/home/devcon_2024.png',
    features: [
      { text: t('collaborations-with-tier-1-crypto-companies') },
      { text: t('30-phygital-stations-with-nfc-chips') },
      { text: t('branded-nfts-with-rewards') },
      { text: t('article-in-cointelegraph') },
    ],
    location: t('bangkok'),
    link: '/devcon-week-2024',
  },
  {
    title: 'Chain Atlas 2025',
    img: '/images/home/chain_atlas_2025.png',
    features: [
      { text: t('gas-free-multi-chain-geo-based-crypto-game') },
      { text: t('1-000-000-prize-pool') },
      { text: t('available-in-150-cities-worldwide') },
      { text: t('major-l1-and-l2-partnerships') },
      { text: t('atlas-ai-agent') },
    ],
    location: t('worldwide'),
    link: '/chainatlas',
  },
  {
    title: 'Burnermap',
    img: '/images/home/burnermap.png',
    features: [
      { text: 'Find friends and places on the playa' },
      { text: 'Mark your camping location' },
      { text: 'Create an event schedule' },
      { text: 'Printable BurnerMap' },
    ],
    location: 'Gerlach, Nevada, USA',
    link: '/burnermap',
  },
]
</script>

<template>
  <div class="pt-7.5 pb-15.5 lg:pt-45 lg:pb-36">
    <div class="lg:max-w-307.5 lg:mx-auto">
      <p
        class="font-medium text-[1.375rem] tracking-wide uppercase px-5 mb-4.5 lg:pl-11"
      >
        {{ t('case-study') }}
      </p>

      <ClientOnly>
        <swiper-container
          :space-between="12"
          :pagination="{ clickable: true }"
          :slides-offset-before="slidesOffsetBefore"
          :slides-offset-after="slidesOffsetAfter"
          :breakpoints="swiperBreakpoints"
        >
          <swiper-slide v-for="(item, index) in caseStudy" :key="index">
            <div
              class="hover-container rounded-[1.25rem] min-h-122.5 h-125 case-study-gradient common-gradient"
            >
              <div class="flex flex-col rounded-[1.25rem] h-full">
                <NuxtImg
                  loading="lazy"
                  format="webp"
                  :src="`/images/home/gradient_case.png`"
                  class="hover-content -z-1 hidden xl:block xl:opacity-0 transition-opacity absolute rounded-[1.25rem] top-0 h-125 w-[calc(100%_-_1px)]"
                  alt=""
                />

                <div class="relative mx-4 pt-4">
                  <NuxtImg
                    loading="lazy"
                    format="webp"
                    class="rounded-2xl w-full object-cover h-46"
                    :src="item.img"
                    alt=""
                  />

                  <div
                    class="absolute bottom-2 left-2 bg-black rounded-full flex flex-row items-center gap-2 pl-1.5 pr-3"
                  >
                    <Icon name="belong:icon-location-violet" class="w-3 h-3" />
                    <p class="text-base font-light">
                      {{ item.location }}
                    </p>
                  </div>
                </div>

                <div
                  class="flex flex-col justify-between mt-3 items-start h-full px-4 pb-4 lg:px-6 lg:pb-5.5"
                >
                  <div class="flex flex-col gap-2">
                    <p class="font-semibold text-[1.375rem] uppercase">
                      {{ item.title }}
                    </p>

                    <div class="flex flex-col gap-3">
                      <div
                        v-for="(feature, i) in item.features"
                        :key="i"
                        class="flex flex-row items-start gap-1"
                      >
                        <Icon
                          name="belong:checkbox-custom"
                          class="min-w-4.5 w-4.5 h-4.5 mt-0.75"
                        />
                        <div
                          class="text-[#C5C5C5] text-base font-light leading-4.75"
                        >
                          {{ feature.text }}

                          <p v-if="feature.highlight" class="italic block">
                            {{ feature.highlight }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <NuxtLink
                    :to="localeRoute(item.link)"
                    target="_blank"
                    class="font-semibold text-xs tracking-wide py-2 px-5.5 rounded-full hover-content xl:opacity-0 transition-opacity"
                    style="
                      background: linear-gradient(
                        90deg,
                        #7540ee 0%,
                        #af55ff 100%
                      );
                    "
                  >
                    {{ t('learn-more') }}
                  </NuxtLink>
                </div>
              </div>
            </div>
          </swiper-slide>
        </swiper-container>
      </ClientOnly>
    </div>
  </div>
</template>

<style>
@reference "tailwindcss";

.case-study-gradient {
  &::before {
    @apply rounded-[1.25rem];
    background: linear-gradient(
      142.08deg,
      rgba(255, 255, 255, 0.28) -5.03%,
      rgba(224, 224, 224, 0.34) 21.3%,
      rgba(101, 84, 169, 0.08) 49.04%,
      rgba(218, 218, 218, 0.2) 70.19%
    );
  }
}

.hover-container {
  &:hover {
    .hover-content {
      @apply opacity-100;
    }
  }
}
</style>
